import { Button } from '@heroui/react'
import type { User } from '@qse/constants'
import { AppRoutesList } from '@qse/constants'
import { FormikControl } from '@qse/ui'
import { detectCurrentApp, getDefaultRoute, storage } from '@qse/utils'
import { Form, Formik } from 'formik'
import { ArrowLeft } from 'lucide-react'
import { useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import * as Yup from 'yup'

import { authService } from '../logic/auth-service'
import { useAuthCheck } from '../logic/use-auth-check'

interface TwoFAVerifyScreenProps {
  onSuccess?: (response: User) => void
  onTOTPCodeRequired?: () => void
}

interface TOTPVerifyFormData {
  code: string
}

interface LocationState {
  fromPasswordChange?: boolean
}

const TwoFAVerify: React.FC<TwoFAVerifyScreenProps> = ({ onSuccess, onTOTPCodeRequired }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const { triggerAuthCheckAfter2FA } = useAuthCheck()

  // Check if user came from password change
  const state = location.state as LocationState | undefined
  const fromPasswordChange = state?.fromPasswordChange ?? false

  // Function to handle back navigation based on context
  const handleBackNavigation = async () => {
    if (fromPasswordChange) {
      // If user came from password change, go back to profile
      navigate('/profile', { replace: true })
    } else {
      // Otherwise, clear everything and go to login
      try {
        // Clear all localStorage values
        storage.localStorage.removeItem('temp_email')
        storage.localStorage.removeItem('temp_session')
        storage.localStorage.removeItem('totp_setup_data')
        storage.localStorage.removeItem('totp_setup_skipped')
        storage.localStorage.removeItem('user_first_login_complete')
        storage.localStorage.removeItem('isMfaVerified')
        storage.localStorage.removeItem('mfaStepCompleted')
        storage.localStorage.removeItem('token')
        storage.localStorage.removeItem('session_expired')

        // Clear browser localStorage as well
        if (typeof window !== 'undefined') {
          localStorage.clear()
          sessionStorage.clear()
        }

        // Sign out from Cognito
        await authService.logout()

        console.log('2FA Verify - Cleared all localStorage values and signed out')

        // Navigate to login
        navigate(AppRoutesList.login, { replace: true })
      } catch (error) {
        console.error('Error during logout:', error)
        // Still navigate to login even if logout fails
        navigate(AppRoutesList.login, { replace: true })
      }
    }
  }

  // Call onTOTPCodeRequired callback when component mounts
  useEffect(() => {
    onTOTPCodeRequired?.()
  }, [onTOTPCodeRequired])

  const totpVerifyValidationSchema = Yup.object({
    code: Yup.string()
      .required('Verification code is required')
      .matches(/^\d{6}$/, 'Please enter a valid 6-digit code')
      .length(6, 'Code must be exactly 6 digits'),
  })

  const handleTOTPVerify = async (
    values: TOTPVerifyFormData,
    {
      setSubmitting,
      setStatus,
    }: {
      setSubmitting?: (isSubmitting: boolean) => void
      setStatus?: (status: string) => void
    },
  ) => {
    try {
      const user = await authService.confirmTOTPCode(values.code)

      // Call onSuccess callback if provided
      onSuccess?.(user)

      // Trigger manual auth check after 2FA completion
      await triggerAuthCheckAfter2FA()

      // Navigate to dashboard
      // Role-based access control (copied from use-login.ts)
      // Role-based navigation using centralized logic
      if (user && user.userRole) {
        const { appType } = detectCurrentApp()
        const defaultRoute = getDefaultRoute(user.userRole, appType)

        navigate(defaultRoute, { replace: true })
      } else {
        // Fallback
        navigate(AppRoutesList.dashboard, { replace: true })
      }
    } catch (error: any) {
      console.log('🚀 ~ handleTOTPVerify ~ error:', error)
      authService.handleError(error, 'mfaVerification', setStatus)
    } finally {
      setSubmitting?.(false)
    }
  }

  return (
    <div className="h-full">
      <div className="sticky top-0 flex lg:justify-end justify-between z-50 w-full px-4 py-4 dark:bg-[#000] bg-white">
        <Button
          className="font-medium"
          startContent={<ArrowLeft size={16} />}
          variant="light"
          onPress={handleBackNavigation}
        >
          {fromPasswordChange ? 'Back to Profile' : 'Back to Login'}
        </Button>
      </div>
      <div className="flex items-center h-[calc(100vh-72px)] w-full lg:pl-[110px] lg:max-w-full max-w-[440px] mx-auto">
        <div className="lg:w-[440px] w-full p-8 lg:p-0 flex flex-col gap-11">
          <div className="flex flex-col gap-2">
            <h1 className="text-[28px] font-medium">Two-Factor Authentication</h1>
            <p className="text-[15px]">
              {fromPasswordChange
                ? 'Your password has been updated. Please verify your 2FA code to continue.'
                : 'Enter the 6-digit code from your authenticator app'}
            </p>
          </div>

          <div className="flex justify-center">
            <div className="w-[80px] h-[80px] bg-blue-50 rounded-full flex items-center justify-center">
              <svg
                className="w-10 h-10 text-blue-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                />
              </svg>
            </div>
          </div>

          <Formik
            initialValues={{ code: '' }}
            validationSchema={totpVerifyValidationSchema}
            onSubmit={handleTOTPVerify}
          >
            {({ isSubmitting, isValid, dirty }) => (
              <Form>
                <div className="flex flex-col gap-[30px]">
                  <div>
                    <FormikControl
                      autoFocus
                      control="text"
                      label="Verification Code"
                      name="code"
                      placeholder="e.g. 123456"
                      radius="sm"
                      size="md"
                      variant="bordered"
                    />
                  </div>
                </div>

                <Button
                  className="w-full mt-6 font-medium"
                  color="primary"
                  isDisabled={!isValid || !dirty}
                  isLoading={isSubmitting}
                  radius="sm"
                  type="submit"
                >
                  Verify & Continue
                </Button>
              </Form>
            )}
          </Formik>

          {/* <div className="text-center space-y-3">
            <p className="text-sm text-gray-600">
              Don't have access to your authenticator app?{' '}
              <button
                className="text-blue-600 hover:text-blue-800 underline"
                type="button"
                onClick={() => navigate(AppRoutesList.login)}
              >
                Try a different method
              </button>
            </p>
          </div> */}
        </div>
      </div>
    </div>
  )
}

export default TwoFAVerify
